import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSessionPersistence } from '../../hooks/useSessionPersistence';
import { ResponsiveContainer, ResponsiveHeader, ResponsiveContent, ResponsiveFooter } from '../ui/ResponsiveLayout';

interface FinalInstructions {
  emergencyProcedures: string;
  documentLocations: string;
  importantContacts: string;
  finalMessage: string;
  specialRequests: string;
}

const Frame244PetsFinal: React.FC = () => {
  const navigate = useNavigate();
  const { session, updateTaskProgress } = useSessionPersistence();

  const [instructions, setInstructions] = useState<FinalInstructions>({
    emergencyProcedures: '',
    documentLocations: '',
    importantContacts: '',
    finalMessage: '',
    specialRequests: ''
  });

  // Helper function to save data directly to localStorage
  const saveToLocalStorage = (instructionsToSave: FinalInstructions) => {
    try {
      const dataToSave = {
        answers: {
          finalInstructions: instructionsToSave
        },
        lastUpdated: new Date().toISOString()
      };

      // Save to backup location for redundancy
      localStorage.setItem('pets-final-backup', JSON.stringify(instructionsToSave));
      localStorage.setItem('pets-final-data-backup', JSON.stringify(dataToSave));

      return true;
    } catch (error) {
        console.error('[Frame244PetsFinal] Direct save failed:', error);
      return false;
    }
  };

  // Load saved data on component mount - PRIORITIZE SESSION FOR CROSS-DEVICE SYNC
  useEffect(() => {
    // Priority 1: Load from session (authoritative source for cross-device sync)
    if (session?.taskProgress?.pets?.answers) {
      const savedData = session.taskProgress.pets.answers.finalInstructions;
      if (savedData) {
        console.log('[Frame244PetsFinal] Loading from session:', savedData);
        setInstructions(savedData);
        return; // Session data takes priority
      }
    }

    // Priority 2: Fall back to localStorage (for same-device persistence)
    try {
      const backupData = localStorage.getItem('pets-final-backup');
      if (backupData) {
        const data = JSON.parse(backupData);
        if (data) {
          console.log('[Frame244PetsFinal] Loading from localStorage backup:', data);
          setInstructions(data);
        }
      }
    } catch (error) {
      console.error('[Frame244PetsFinal] Failed to load from backup:', error);
    }
  }, [session?.taskProgress?.pets]);

  const handleInputChange = (field: keyof FinalInstructions, value: string) => {
    const newInstructions = {
      ...instructions,
      [field]: value
    };

    setInstructions(newInstructions);

    // IMMEDIATE DIRECT SAVE
    saveToLocalStorage(newInstructions);

    // Also save to session (but don't wait for it)
    updateTaskProgress('pets', {
      finalInstructions: newInstructions
    }).catch(error => {
      console.error('[Frame244PetsFinal] Session save failed:', error);
    });
  };

  const handleComplete = async () => {
    try {
      // IMMEDIATE DIRECT SAVE
      if (!saveToLocalStorage(instructions)) {
        throw new Error('Failed to save to local storage');
      }

      // Save to session and wait for it to complete
      await updateTaskProgress('pets', {
        finalInstructions: instructions
      });

      // Navigate to summary page
      navigate('/frame-248');
    } catch (error) {
      console.error('[Frame244PetsFinal] Save failed:', error);

      // Show error to user
      const message =
        'Failed to save your changes. Please try again. If the problem persists, your session may have expired - try refreshing the page.';
      if (confirm(message + '\n\nClick OK to try again or Cancel to discard changes.')) {
        // Try again
        handleComplete();
      } else {
        // User chose to discard changes
        navigate('/frame-140');
      }
    }
  };

  const handleBack = async () => {
    try {
      // IMMEDIATE DIRECT SAVE
      if (!saveToLocalStorage(instructions)) {
        throw new Error('Failed to save to local storage');
      }

      // Save to session and wait for it to complete
      await updateTaskProgress('pets', {
        finalInstructions: instructions
      });

      // Only navigate if both saves were successful
      navigate('/frame-243');
    } catch (error) {
      console.error('[Frame244PetsFinal] Save failed:', error);

      // Show error to user
      const message =
        'Failed to save your changes. Please try again. If the problem persists, your session may have expired - try refreshing the page.';
      if (confirm(message + '\n\nClick OK to try again or Cancel to discard changes.')) {
        // Try again
        handleBack();
      } else {
        // User chose to discard changes
        navigate('/frame-243');
      }
    }
  };

  // ✅ Only show the banner after the user enters *anything* in any field.
  const showCompletionBanner = Object.values(instructions).some(v => v.trim().length > 0);

  return (
    <ResponsiveContainer>
      <ResponsiveContent>
        <ResponsiveHeader title="Final Instructions and Completion" />

        <div className="max-w-4xl">
          <div className="text-stone-800 text-lg font-medium font-sans mb-8">
            Complete your pets and dependents care plan with final instructions and important information
          </div>

          <div className="space-y-8">
            {/* Emergency Procedures */}
            <div>
              <label className="block text-stone-800 text-base font-medium font-sans mb-4">
                Emergency Procedures
              </label>
              <textarea
                value={instructions.emergencyProcedures}
                onChange={(e) => handleInputChange('emergencyProcedures', e.target.value)}
                className="w-full px-3 py-2 border border-stone-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-700"
                placeholder="What should be done in case of emergency? Who should be contacted first? Any specific procedures to follow?"
                rows={4}
              />
            </div>

            {/* Document Locations */}
            <div>
              <label className="block text-stone-800 text-base font-medium font-sans mb-4">
                Important Document Locations
              </label>
              <textarea
                value={instructions.documentLocations}
                onChange={(e) => handleInputChange('documentLocations', e.target.value)}
                className="w-full px-3 py-2 border border-stone-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-700"
                placeholder="Where are important documents stored? (Pet records, medical files, legal documents, insurance policies, etc.)"
                rows={4}
              />
            </div>

            {/* Important Contacts */}
            <div>
              <label className="block text-stone-800 text-base font-medium font-sans mb-4">
                Important Contacts Summary
              </label>
              <textarea
                value={instructions.importantContacts}
                onChange={(e) => handleInputChange('importantContacts', e.target.value)}
                className="w-full px-3 py-2 border border-stone-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-700"
                placeholder="List all important contacts in one place (veterinarians, guardians, caregivers, emergency contacts, etc.)"
                rows={4}
              />
            </div>

            {/* Special Requests */}
            <div>
              <label className="block text-stone-800 text-base font-medium font-sans mb-4">
                Special Requests
              </label>
              <textarea
                value={instructions.specialRequests}
                onChange={(e) => handleInputChange('specialRequests', e.target.value)}
                className="w-full px-3 py-2 border border-stone-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-700"
                placeholder="Any special requests or considerations for the care of your pets and dependents?"
                rows={4}
              />
            </div>

            {/* Final Message */}
            <div>
              <label className="block text-stone-800 text-base font-medium font-sans mb-4">
                Final Message to Caregivers
              </label>
              <textarea
                value={instructions.finalMessage}
                onChange={(e) => handleInputChange('finalMessage', e.target.value)}
                className="w-full px-3 py-2 border border-stone-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-700"
                placeholder="A personal message to those who will care for your pets and dependents, expressing your gratitude and any final thoughts"
                rows={4}
              />
            </div>
          </div>

          {/* ✅ Completion Message (only after any input exists) */}
          {showCompletionBanner && (
            <div className="mt-12 p-6 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 text-green-600 mt-0.5 flex-shrink-0">
                  <svg fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <div>
                  <h3 className="text-green-800 font-medium text-lg mb-2">
                    Your pets and dependents care plan is almost complete!
                  </h3>
                  <p className="text-green-700 text-base leading-relaxed">
                    You've documented comprehensive care arrangements for your beloved pets and dependents.
                    This information will provide peace of mind and ensure your loved ones receive the best possible care.
                    Click "Complete" to finalize this section and return to your estate plan dashboard.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </ResponsiveContent>

      <ResponsiveFooter
        title="Plan for Your Pets and Dependents"
        subtitle="Provide final instructions for caregivers"
        progressPercent={80}
        onBack={handleBack}
        onNext={handleComplete}
        nextText="Next"
        backText="Back"
      />
    </ResponsiveContainer>
  );
};

export default Frame244PetsFinal;